import Swal from 'sweetalert2';

function decodeTicket(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
}

function encodeTicket(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function parseMoeda(raw: string): number {
  if (raw.includes(',')) {
    return Number(raw.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return Number(raw) || 0;
}

function formatMoedaBr(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isLinhaValor(linha: string): boolean {
  return /=/.test(linha) && /R\$/i.test(linha);
}

function isLinhaPrePatio(linha: string): boolean {
  return isLinhaValor(linha) && /pr[eé][\s-]*p[aá]tio/i.test(linha);
}

function isLinhaDemaisCobrancas(linha: string): boolean {
  if (!isLinhaValor(linha) || isLinhaPrePatio(linha)) return false;
  if (/valor total/i.test(linha)) return false;
  return /p[aá]tio|di[aá]ria|estacionamento/i.test(linha);
}

function extrairValorLinha(linha: string): number {
  const match = linha.match(/R\$\s*([\d.]+,\d{2}|\d+(?:[.,]\d+)?)/i);
  if (!match) return 0;
  return parseMoeda(match[1]);
}

function valorPrePatio(texto: string): number {
  const linha = texto.split(/\r?\n/).find(isLinhaPrePatio);
  if (!linha) return 0;
  return extrairValorLinha(linha);
}

function valorDemaisCobrancas(texto: string): number {
  return texto
    .split(/\r?\n/)
    .filter(isLinhaDemaisCobrancas)
    .reduce((acc, linha) => acc + extrairValorLinha(linha), 0);
}

function semLinhas(texto: string, predicado: (linha: string) => boolean): string {
  return texto
    .split(/\r?\n/)
    .filter(linha => !predicado(linha))
    .join('\n');
}

const regexValorMonetario = /R\$\s*[\d.]+,\d{2}|R\$\s*[\d]+(?:[.,]\d+)?/i;

/** Atualiza totais impressos para refletir apenas as linhas de cobrança restantes no ticket. */
export function ajustarTotaisTicket(texto: string, novoTotal: number): string {
  const formatado = formatMoedaBr(novoTotal);
  const substituirValor = (linha: string) => linha.replace(regexValorMonetario, `R$ ${formatado}`);

  let result = texto
    .split(/\r?\n/)
    .map(linha => {
      if (/VALOR TOTAL:/i.test(linha) || /Total pago:/i.test(linha)) {
        return substituirValor(linha);
      }
      return linha;
    })
    .join('\n');

  result = result.replace(
    /(Declaro o recebimento no valor de:\r?\n)R\$\s*[^\r\n]+/i,
    `$1R$ ${formatado}`
  );

  return result;
}

/** Divide o ticket em dois comprovantes (pré-pátio e demais cobranças) e totais coerentes. */
export function montarTicketsSeparados(texto: string): [string, string] {
  const vlPre = valorPrePatio(texto);
  const vlPatio = valorDemaisCobrancas(texto);

  const somentePrePatio = ajustarTotaisTicket(semLinhas(texto, isLinhaDemaisCobrancas), vlPre);
  const semPrePatio = ajustarTotaisTicket(semLinhas(texto, isLinhaPrePatio), vlPatio);

  return [somentePrePatio, semPrePatio];
}

export async function decidirImpressaoPrePatio(base64: string): Promise<string[] | null> {
  let texto: string;
  try {
    texto = decodeTicket(base64);
  } catch {
    return [base64];
  }

  if (valorPrePatio(texto) <= 0) {
    return [base64];
  }

  const result = await Swal.fire({
    title: 'Impressão do pré-pátio',
    text: 'O pré-pátio deve sair junto com o pátio ou em impressão separada? Em "Junto", um único ticket traz os dois valores. Em "Separado", cada ticket traz apenas o valor correspondente.',
    icon: 'question',
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Junto',
    denyButtonText: 'Separado',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#f36e25',
    denyButtonColor: '#1e3a8a'
  });

  if (result.isConfirmed) {
    return [base64];
  }

  if (result.isDenied) {
    const [prePatio, patio] = montarTicketsSeparados(texto);
    return [encodeTicket(prePatio), encodeTicket(patio)];
  }

  return null;
}
